import { makeStyles, createStyles, alpha } from "@material-ui/core/styles";
import type { Theme } from "src/theme";

export const useDialogStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: "70vw",
      width: "100%",
      height: "90vh",
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down("md")]: {
        maxWidth: "90vw",
      },
    },
    content: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.default,
      flex: 1,
      overflowY: "auto",
    },
    cardGrid: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    emptyState: {
      textAlign: "center",
      padding: theme.spacing(5, 2),
      color: theme.palette.text.secondary,
      gridColumn: "1 / -1",
    },
    batchIndicator: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      backgroundColor: alpha(theme.palette.primary.dark, 0.08),
      borderRadius: theme.shape.borderRadius,
      textAlign: "center",
      color: theme.palette.primary.dark,
      border: `1px dashed ${theme.palette.primary.dark}`,
    },
  })
);

export const useCardStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      "&:hover": {
        borderColor: theme.palette.action.disabled,
      },
    },
    cardExpanded: {
      boxShadow: theme.shadows[2],
      borderColor: theme.palette.primary.dark,
    },
    cardCompleted: {
      background: alpha(theme.palette.success.main, 0.08),
      borderColor: theme.palette.success.main,
    },
    cardSuccess: {
      animation: "$backgroundFlash 0.5s ease-out forwards",
    },
    cardHeader: {
      padding: theme.spacing(1.5, 2),
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    cardHeaderLeft: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    cardTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    cardTotal: {
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
    cardCount: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      background: theme.palette.background.default,
      padding: "4px 10px",
      borderRadius: 12,
      fontWeight: 500,
    },
    cardCountCompleted: {
      background: alpha(theme.palette.success.main, 0.08),
      color: theme.palette.success.main,
    },
    cardCountSuccess: {
      background: alpha(theme.palette.success.main, 0.15),
      color: theme.palette.success.main,
      transition: theme.transitions.create(["background", "color"], {
        duration: 400,
      }),
    },
    cardContent: {
      padding: theme.spacing(0, 2, 1.5),
    },
    toggleButton: {
      justifyContent: "center",
      gap: theme.spacing(1),
      width: "100%",
      padding: theme.spacing(1, 2),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.primary,
      textTransform: "none" as const,
      transition: theme.transitions.create([
        "background-color",
        "border-color",
        "color",
      ]),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.dark, 0.08),
        borderColor: theme.palette.primary.dark,
        color: theme.palette.primary.dark,
      },
    },
    toggleButtonSuccess: {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      borderColor: theme.palette.success.main,
      color: theme.palette.success.main,
      transition: theme.transitions.create(
        ["background-color", "border-color", "color"],
        { duration: 400 }
      ),
    },
    compactButtonGroup: {
      display: "flex",
      gap: theme.spacing(1),
    },
    approvalContainer: {
      minHeight: 36,
    },
    approveAllButton: {
      transition: theme.transitions.create(["opacity", "transform"]),
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 0),
      animation: "$fadeIn 0.3s ease-out",
    },
    loadingBar: {
      width: "100%",
      borderRadius: 4,
      height: 4,
      backgroundColor: alpha(theme.palette.primary.dark, 0.15),
      "& .MuiLinearProgress-bar": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    loadingText: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    successContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 0),
      animation: "$successPop 0.4s ease-out",
    },
    successIcon: {
      color: theme.palette.success.main,
      fontSize: 48,
      animation: "$checkPop 0.4s ease-out",
    },
    successText: {
      fontSize: 14,
      color: theme.palette.success.main,
      fontWeight: 600,
    },
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 1,
      },
    },
    "@keyframes successPop": {
      "0%": {
        opacity: 0,
        transform: "scale(0.8)",
      },
      "50%": {
        transform: "scale(1.05)",
      },
      "100%": {
        opacity: 1,
        transform: "scale(1)",
      },
    },
    "@keyframes checkPop": {
      "0%": {
        transform: "scale(0) rotate(-45deg)",
      },
      "40%": {
        transform: "scale(1.4) rotate(10deg)",
      },
      "70%": {
        transform: "scale(0.9) rotate(-3deg)",
      },
      "100%": {
        transform: "scale(1) rotate(0deg)",
      },
    },
    "@keyframes backgroundFlash": {
      "0%": {
        backgroundColor: theme.palette.background.paper,
      },
      "100%": {
        backgroundColor: alpha(theme.palette.success.main, 0.08),
      },
    },
    transactionListContainer: {
      display: "grid",
      gridTemplateRows: "0fr",
      transition: theme.transitions.create("grid-template-rows", {
        duration: 250,
      }),
    },
    transactionListContainerExpanded: {
      gridTemplateRows: "1fr",
    },
    transactionListInner: {
      overflow: "hidden",
    },
    transactionList: {
      background: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius / 2,
      marginBottom: theme.spacing(1.5),
      maxHeight: 240,
      overflowY: "auto",
      border: `1px solid ${theme.palette.divider}`,
    },
    transactionListHeader: {
      padding: theme.spacing(1, 1.5),
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.default,
      position: "sticky",
      top: 0,
      display: "flex",
      alignItems: "center",
      gap: 8,
      zIndex: 1,
    },
    buttonGroup: {
      display: "flex",
      gap: theme.spacing(1),
      justifyContent: "flex-end",
    },
    completedState: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: theme.spacing(2),
      color: theme.palette.success.main,
      fontWeight: 500,
      fontSize: 14,
    },
    completedIcon: {
      width: 20,
      height: 20,
    },
    expandIcon: {
      transition: theme.transitions.create("transform"),
      color: theme.palette.text.secondary,
    },
    expandIconExpanded: {
      transform: "rotate(180deg)",
    },
  })
);

export const useRecategorizeDialogStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      paddingTop: theme.spacing(1),
    },
    subtitle: {
      marginBottom: theme.spacing(2),
    },
    select: {
      width: "100%",
    },
    disabledOption: {
      color: theme.palette.text.disabled,
      width: "100%",
      pointerEvents: "auto" as const,
    },
  })
);

// XGrid styles for transaction table inside cards (card-style cells)
export const useTransactionXGridStyles = makeStyles((theme: Theme) =>
  createStyles({
    viewModeButton: {
      padding: theme.spacing(0.75),
    },
    viewModeButtonActive: {
      color: theme.palette.primary.main,
    },
    selectAllLabel: {
      cursor: "pointer",
    },
    cellContainer: {
      width: "100%",
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
    },
    cellDate: {
      marginBottom: theme.spacing(0.5),
    },
    cellRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(0.5),
      paddingRight: theme.spacing(2),
    },
    cellAmountNegative: {
      color: theme.palette.success.main,
    },
    gridContainer: {
      marginBottom: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius / 2,
      overflow: "hidden",
      // Responsive height: use 50% of viewport height, with min 300px and max 600px
      height: "clamp(300px, 50vh, 600px)",
      [theme.breakpoints.up("lg")]: {
        // On larger screens, allow more height
        height: "clamp(400px, 55vh, 700px)",
      },
    },
    // Table view container - can fit more rows
    gridContainerTable: {
      height: "clamp(250px, 45vh, 500px)",
      [theme.breakpoints.up("lg")]: {
        height: "clamp(300px, 50vh, 600px)",
      },
    },
    root: {
      border: "none",
      backgroundColor: theme.palette.background.paper,
      fontSize: 13,
      "& .MuiDataGrid-main": {
        backgroundColor: theme.palette.background.paper,
      },
      // Hide column headers (card view)
      "& .MuiDataGrid-columnsContainer": {
        display: "none",
      },
      "& .MuiDataGrid-columnHeaders": {
        display: "none",
      },
      "& .MuiDataGrid-iconSeparator": {
        display: "none",
      },
      "& .MuiDataGrid-columnSeparator": {
        display: "none",
      },
      // Cells
      "& .MuiDataGrid-cell": {
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRight: "none",
        padding: theme.spacing(0, 1),
        "&:focus": {
          outline: "none",
        },
        "&:focus-within": {
          outline: "none",
        },
      },
      // Rows
      "& .MuiDataGrid-row": {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.dark, 0.08),
        },
        "&.Mui-selected": {
          backgroundColor: alpha(theme.palette.primary.dark, 0.08),
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.dark, 0.08),
          },
        },
        "&:last-child .MuiDataGrid-cell": {
          borderBottom: "none",
        },
      },
      // Checkbox column
      "& .MuiDataGrid-cellCheckbox": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      "& .MuiDataGrid-columnHeaderCheckbox": {
        display: "none",
      },
      // Toolbar
      "& .MuiDataGrid-toolbarContainer": {
        padding: theme.spacing(1, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        minHeight: 44,
      },
      // Footer (hidden)
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
    },
    // Table view styles - show headers
    tableViewRoot: {
      "& .MuiDataGrid-columnsContainer": {
        display: "flex",
      },
      "& .MuiDataGrid-columnHeaders": {
        display: "flex",
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      "& .MuiDataGrid-columnHeader": {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        "&:focus": {
          outline: "none",
        },
        "&:focus-within": {
          outline: "none",
        },
      },
      "& .MuiDataGrid-columnSeparator": {
        display: "none",
      },
      // Show header checkbox in table view
      "& .MuiDataGrid-columnHeaderCheckbox": {
        display: "flex",
      },
    },
  })
);
